/**
 * 文件定位：
 * 1. 当前文件位于 web-client/views/editor；
 * 2. 负责把通用删除确认内容组件接入 NaiveUI dialog；
 * 3. 特征删除与草图删除共享该入口，避免确认框结构在多个动作处理器中重复实现。
 */

import {h} from 'vue';
import FeatureTreeDeleteConfirmDialog from '@/components/dialog/FeatureTreeDeleteConfirmDialog.vue';

/**
 * 删除确认项目类型。
 */
export type FeatureTreeDeleteConfirmItemKind =
    | 'feature'
    | 'sketch'
    | 'mate'
    | 'linear-component-pattern'
    | 'part'
    | 'assembly';

/**
 * 删除确认项目。
 */
export interface FeatureTreeDeleteConfirmItem {
    /** 稳定 ID。 */
    readonly id: string;
    /** 设计树显示名称，不包含类型后缀。 */
    readonly label: string;
    /** 项目类型。 */
    readonly kind: FeatureTreeDeleteConfirmItemKind;
}

/**
 * 删除确认对话框端口。
 */
export interface FeatureTreeDeleteConfirmationDialogPort {
    /** 创建对话框。 */
    readonly create: (options: Record<string, unknown>) => unknown;
}

/**
 * 删除确认请求输入。
 */
export interface RequestFeatureTreeDeleteConfirmationInput {
    /** NaiveUI dialog 实例。 */
    readonly dialog: FeatureTreeDeleteConfirmationDialogPort;
    /** 待删除目标。 */
    readonly target: FeatureTreeDeleteConfirmItem;
    /** 会一起删除的相关项目。 */
    readonly relatedItems?: readonly FeatureTreeDeleteConfirmItem[];
    /** 对话框标题；仅供复用确认结构的其他操作覆盖。 */
    readonly title?: string;
    /** 目标项上方的操作提示；默认保持删除语义。 */
    readonly targetPrompt?: string;
    /** 从属项列表上方的影响提示；默认保持删除语义。 */
    readonly relatedItemsPrompt?: string;
}

/**
 * 弹出设计树删除确认框。
 *
 * 设计说明：
 * 1. SolidWorks 删除任意特征/草图都会先确认目标项，相关项目列表为空时也保留列表区域；
 * 2. 该函数只负责用户确认结果，不知道也不执行具体删除动作；
 * 3. 弹窗内容组件固定使用 `n-input disabled` 与 `PuppyInputSelect`，保持所有设计树删除入口一致。
 *
 * @param input 删除确认请求。
 * @returns 用户是否确认删除。
 */
export function requestFeatureTreeDeleteConfirmation(
    input: RequestFeatureTreeDeleteConfirmationInput,
): Promise<boolean> {
    return new Promise((resolve) => {
        let resolved = false;

        /**
         * 对话框可能从按钮或关闭图标结束，统一收口避免重复 resolve。
         */
        const finish = (value: boolean): void => {
            if (resolved) {
                return;
            }
            resolved = true;
            resolve(value);
        };

        input.dialog.create({
            title: input.title ?? '确认删除',
            content: () => h(FeatureTreeDeleteConfirmDialog, {
                target: input.target,
                relatedItems: input.relatedItems ?? [],
                targetPrompt: input.targetPrompt,
                relatedItemsPrompt: input.relatedItemsPrompt,
            }),
            positiveText: '是',
            negativeText: '取消',
            onPositiveClick: () => {
                finish(true);
                return true;
            },
            onNegativeClick: () => {
                finish(false);
            },
            onClose: () => {
                finish(false);
            },
        });
    });
}
